import { ArrowDown, ArrowUp, Info } from 'lucide-react';
import { HTMLAttributes, ReactNode } from 'react';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { Tooltip } from '../../../../design-system/tooltip/tooltip';
import { cn } from '../../../../utils/cn';

export type TrendDirection = 'increasing' | 'decreasing' | 'default';
export type ValueTone = 'positive' | 'negative' | 'neutral';

type MetricCardProps = HTMLAttributes<HTMLDivElement> & {
  /** Top-left label */
  label: string;
  /** Bottom-left main value (already formatted if needed) */
  value: string | number;
  /** Top-right trend value (e.g. 5%, £22,975). If omitted, the trend section is hidden */
  trend?: string | number;
  /** Visual direction for the trend. Controls arrow and color */
  direction?: TrendDirection;
  /** Bottom-right supporting text (e.g. from last month) */
  subText?: string;
  /** Whether to color the main value as positive/negative */
  valueTone?: ValueTone;
  /** Custom className for the root */
  className?: string;
  /** tooltip text  */
  tooltipText?: string | ReactNode;
};

const getTrendClasses = (tone: MetricCardProps['valueTone']) => {
  if (tone === 'positive') return 'text-seapoint-green';
  if (tone === 'negative') return 'text-seapoint-red';
  return 'text-seapoint-gray-text';
};

export const MetricCard = ({
  label,
  value,
  trend,
  direction = 'default',
  subText,
  valueTone = 'neutral',
  className,
  tooltipText,
  ...divProps
}: MetricCardProps) => {
  const showTrend = typeof trend !== 'undefined' && trend !== null && `${trend}`.length > 0;

  return (
    <div
      {...divProps}
      className={cn(
        'w-full rounded-[10px] border-[0.5px] border-seapoint-gray-border bg-white px-5 py-4',
        // Allow consumers to size the card; default content layout matches the provided spec
        className,
      )}
    >
      <Inline justify="between" align="center" className="w-full" wrap="nowrap">
        {/* Left side (label + value) */}
        <Stack space={1}>
          <Inline space={1} align="center">
            <p className="text-xs font-normal tracking-[-0.02em] text-seapoint-gray-text">{label}</p>
            {tooltipText && (
              <Tooltip
                contentAlign="center"
                trigger={<Info size={12} className="text-seapoint-gray-text" />}
                content={tooltipText}
              />
            )}
          </Inline>
          <p className="text-xl font-medium">{value}</p>
        </Stack>

        {/* Right side (trend + sub text) */}
        <Stack space={1} align="end" className="text-right">
          {showTrend && (
            <Inline space={1} align="center" justify="end" wrap="nowrap" className={getTrendClasses(valueTone)}>
              {direction === 'increasing' && <ArrowUp size={16} strokeWidth={2} aria-label="trend up" />}
              {direction === 'decreasing' && <ArrowDown size={16} strokeWidth={2} aria-label="trend down" />}
              {direction === 'default' && <span className="sr-only" aria-label="trend neutral" />}
              <span className={cn('text-sm font-medium tracking-[-0.06em]', getTrendClasses(valueTone))}>{trend}</span>
            </Inline>
          )}

          {subText && <p className="text-xs font-normal tracking-[-0.02em] text-seapoint-gray-text">{subText}</p>}
        </Stack>
      </Inline>
    </div>
  );
};
